// S26: Admin Users List Page
import { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Space,
  Input,
  Select,
  Card,
  Button,
  Typography,
  message,
  InputNumber,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import {
  adminUsersEndpoints,
  type UserListItem,
  type UserFilters,
} from '../api/adminUsersEndpoints';

const { Text } = Typography;

export function AdminUsers() {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const navigate = useNavigate();

  // Filters
  const [roleFilter, setRoleFilter] = useState<'admin' | 'user' | undefined>(undefined);
  const [countryFilter, setCountryFilter] = useState<'USA' | 'NON_USA' | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | undefined>(undefined);
  const [minBasePrincipal, setMinBasePrincipal] = useState<number | undefined>(undefined);
  const [maxBasePrincipal, setMaxBasePrincipal] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const filters: UserFilters = {
        page: pagination.current,
        per: pagination.pageSize,
        role: roleFilter,
        country: countryFilter,
        status: statusFilter,
        min_base_principal: minBasePrincipal,
        max_base_principal: maxBasePrincipal,
        search: searchText || undefined,
      };

      const data = await adminUsersEndpoints.getUsers(filters);

      setUsers(data.users);
      setPagination(prev => ({
        ...prev,
        total: data.pagination.total_count,
      }));
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: newPagination.total,
    });
  };

  const handleReset = () => {
    setRoleFilter(undefined);
    setCountryFilter(undefined);
    setStatusFilter(undefined);
    setMinBasePrincipal(undefined);
    setMaxBasePrincipal(undefined);
    setSearchText('');
    setPagination(prev => ({ ...prev, current: 1 }));
    setTimeout(fetchUsers, 0);
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchUsers();
  };

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  const columns: ColumnsType<UserListItem> = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 200,
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role: string) => (
        <Tag icon={role === 'admin' ? <TeamOutlined /> : <UserOutlined />} color={role === 'admin' ? 'purple' : 'blue'}>
          {role.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 100,
      render: (country: string) => (
        <Tag color={country === 'USA' ? 'blue' : 'green'}>{country}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active: boolean) => (
        <Tag icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={active ? 'success' : 'default'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Pool Status',
      dataIndex: 'participation_status',
      key: 'participation_status',
      width: 120,
      render: (status?: string) => {
        if (!status || status === 'active') return <Tag color="green">Active</Tag>;
        if (status === 'pending_close') return <Tag color="orange">Pending Close</Tag>;
        if (status === 'exited') return <Tag color="default">Exited</Tag>;
        return null;
      },
    },
    {
      title: 'Base Principal',
      dataIndex: 'base_principal_cents',
      key: 'base_principal_cents',
      width: 150,
      render: (cents: number) => <Text strong>{formatCurrency(cents)}</Text>,
      sorter: (a, b) => a.base_principal_cents - b.base_principal_cents,
    },
    {
      title: 'Withdrawable',
      dataIndex: 'withdrawable_cents',
      key: 'withdrawable_cents',
      width: 150,
      render: (cents: number) => formatCurrency(cents),
      sorter: (a, b) => a.withdrawable_cents - b.withdrawable_cents,
    },
    {
      title: 'Earning',
      dataIndex: 'eligible_for_earnings',
      key: 'eligible_for_earnings',
      width: 100,
      render: (eligible: boolean) => (
        <Tag color={eligible ? 'green' : 'orange'}>
          {eligible ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/admin/users/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage all users and view their account status"
      />

      <Card style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Select
              placeholder="Filter by Role"
              style={{ width: 150 }}
              value={roleFilter}
              onChange={setRoleFilter}
              allowClear
            >
              <Select.Option value="admin">Admin</Select.Option>
              <Select.Option value="user">User</Select.Option>
            </Select>

            <Select
              placeholder="Filter by Country"
              style={{ width: 150 }}
              value={countryFilter}
              onChange={setCountryFilter}
              allowClear
            >
              <Select.Option value="USA">USA</Select.Option>
              <Select.Option value="NON_USA">NON_USA</Select.Option>
            </Select>

            <Select
              placeholder="Filter by Status"
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
            >
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>

            <InputNumber
              placeholder="Min Principal"
              style={{ width: 150 }}
              value={minBasePrincipal}
              onChange={(val) => setMinBasePrincipal(val || undefined)}
              prefix="$"
              min={0}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />

            <InputNumber
              placeholder="Max Principal"
              style={{ width: 150 }}
              value={maxBasePrincipal}
              onChange={(val) => setMaxBasePrincipal(val || undefined)}
              prefix="$"
              min={0}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />

            <Input
              placeholder="Search name or email"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: 250 }}
              prefix={<SearchOutlined />}
            />

            <Button icon={<SearchOutlined />} type="primary" onClick={handleSearch}>
              Search
            </Button>

            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </Space>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
            pageSizeOptions: ['10', '25', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>
    </div>
  );
}
